import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function MississaugaServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Mississauga',
      description: 'Professional alarm system installation and monitoring for Mississauga homes and businesses. 24/7 protection with instant mobile alerts.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Mississauga',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Mississauga properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Mississauga',
      description: 'Commercial card access and keyless entry systems for Mississauga businesses. Secure entry management with detailed logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Mississauga',
      description: 'Real-time video surveillance monitoring for Mississauga properties. Watch your property from anywhere, anytime.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Port Credit', 'Streetsville', 'Meadowvale', 'Erin Mills', 'Clarkson',
    'Lakeview', 'Cooksville', 'Malton', 'Churchill Meadows', 'Creditview',
    'Hurontario', 'Square One District', 'Lorne Park', 'Mineola', 'Applewood'
  ];

  const stats = [
    { number: '12,000+', label: 'Mississauga Installations' },
    { number: '25+', label: 'Years Serving Mississauga' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 1hr', label: 'Response Time' }
  ];

  const localInsights = [
    {
      icon: 'ri-police-badge-line',
      stat: '38%',
      label: 'Increase in Peel Region Property Crime (2023)',
      description: 'Peel Regional Police data shows rising property crime rates in Mississauga neighborhoods, making professional security systems a critical investment for homeowners and businesses.'
    },
    {
      icon: 'ri-home-smile-line',
      stat: '156,000+',
      label: 'New Residential Units Built Since 2015',
      description: 'Mississauga\'s rapid suburban growth in areas like Churchill Meadows and Erin Mills has created increased demand for modern smart security solutions in new developments.'
    },
    {
      icon: 'ri-building-2-line',
      stat: '73%',
      label: 'Mississauga Businesses Require Access Control',
      description: 'With major corporate headquarters and business parks near Square One and Airport Road, commercial properties need advanced access control and video surveillance systems.'
    }
  ];

  const faqs = [
    {
      question: 'Do you serve all Mississauga neighborhoods including Port Credit and Streetsville?',
      answer: 'Yes! We serve all Mississauga neighborhoods including Port Credit, Streetsville, Meadowvale, Erin Mills, Clarkson, Lakeview, Cooksville, Malton, Churchill Meadows, Creditview, Hurontario, Square One District, Lorne Park, Mineola, and Applewood. Our main office is located in Mississauga at 2355 Derry Road East, ensuring the fastest response times in the GTA.'
    },
    {
      question: 'How fast is your response time in Mississauga?',
      answer: 'Since our main office is located in Mississauga, we offer the fastest response times in the city—typically under 1 hour for emergency service calls. For scheduled installations and consultations, we offer same-day appointments throughout Mississauga. Being locally based means we understand Mississauga neighborhoods and can provide faster, more personalized service.'
    },
    {
      question: 'Can you integrate security systems with Mississauga smart home setups?',
      answer: 'Absolutely! We specialize in smart home security integration for Mississauga properties. Our systems work seamlessly with popular platforms like Google Home, Amazon Alexa, and Apple HomeKit. We can integrate alarm systems, cameras, door locks, and lighting controls into one unified smart home ecosystem that you control from your smartphone.'
    }
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Mississauga', item: 'https://alliancesecuritysystems.com/service-areas/mississauga' }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/mississauga#business',
      name: 'Alliance Security Systems - Mississauga',
      image: 'https://public.readdy.ai/ai/img_res/859d6e0a03404c69351d3f0c9f412133.jpg',
      telephone: '+1-888-458-9181',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2355 Derry Road East, Unit 1',
        addressLocality: 'Mississauga',
        addressRegion: 'ON',
        postalCode: 'L5S 1V6',
        addressCountry: 'CA'
      },
      geo: { '@type': 'GeoCoordinates', latitude: '43.5890', longitude: '-79.6441' },
      url: 'https://alliancesecuritysystems.com/service-areas/mississauga',
      priceRange: '$$',
      areaServed: {
        '@type': 'City',
        name: 'Mississauga',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario' }
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Mississauga Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Mississauga', description: 'Professional alarm system installation and 24/7 monitoring for Mississauga homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Mississauga', description: 'HD and 4K security camera installation with remote viewing for Mississauga properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Mississauga', description: 'Commercial card access and keyless entry systems for Mississauga businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Smart Home Security Mississauga', description: 'Integrated smart home security solutions for modern Mississauga homes.' } }
        ]
      },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '650', bestRating: '5' }
    }
  ];

  return (
    <>
      <SEO
        title="Security Systems Mississauga | Alarm & Camera Installation | Local Experts"
        description="Mississauga's local security company since 1999. Professional alarm systems, security cameras, and access control. 12,000+ Mississauga installations. Office at 2355 Derry Rd E."
        keywords="security systems Mississauga, alarm systems Mississauga, security cameras Mississauga, access control Mississauga, Port Credit security, Streetsville security, Mississauga security company"
        canonical="/service-areas/mississauga"
        structuredData={structuredData}
        faqItems={faqs}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">
                1-888-458-9181
              </a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Home</Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Service Areas</Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">1-888-458-9181</a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Mississauga%20city%20skyline%20at%20sunset%20with%20modern%20buildings%20and%20waterfront%20view%20professional%20urban%20photography%20warm%20lighting%20Square%20One%20area&width=1920&height=1080&seq=mississauga1&orientation=landscape" 
              alt="Mississauga Skyline"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Mississauga, Ontario</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Security Systems Mississauga</strong> - Your Local Security Experts
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
                As Canada's third-largest city and home to our main office, Mississauga receives our fastest response times and most personalized service. With over 12,000 installations across Port Credit, Streetsville, Erin Mills, and beyond, we understand the unique security needs of Mississauga's diverse neighborhoods—from lakefront properties to Square One condos and corporate business parks.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link 
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote in Mississauga
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </Link>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Service Areas</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-[#D4A853] font-medium" itemProp="name">Mississauga</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Security Insights */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                MISSISSAUGA SECURITY INSIGHTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Why Mississauga Properties Need Professional Security
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Mississauga-specific security challenges and solutions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-gray-100 hover:shadow-xl transition-all">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${insight.icon} text-2xl sm:text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-2">{insight.stat}</p>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-3">{insight.label}</h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                MISSISSAUGA SECURITY SERVICES
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Security Solutions in Mississauga
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive security services tailored for Mississauga homes and businesses
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                MISSISSAUGA FAQ
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Frequently Asked Questions About Mississauga Security
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-base sm:text-lg font-semibold text-[#0A1628] pr-4">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Blog Posts */}
        <RelatedBlogPosts city="mississauga" />

        {/* Mississauga Security Resource Centre */}
        <section className="py-16 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center px-4 py-2 bg-[#D4A853]/20 rounded-full mb-4">
                <i className="ri-book-open-line text-[#D4A853] mr-2"></i>
                <span className="text-sm font-semibold text-[#D4A853] uppercase tracking-wide">Mississauga Security Resource Centre</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                In-Depth Mississauga Security Guides
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto text-lg">
                Independent research covering every aspect of home and commercial security in Mississauga — from Peel Region crime data to Pearson Airport cargo protection.
              </p>
            </div>

            {/* Two residential guide cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Alarm Systems Guide */}
              <a href="/blog/best-alarm-system-company-mississauga" className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20Mississauga%20suburban%20neighbourhood%20at%20golden%20hour%20with%20large%20detached%20home%20featuring%20professional%20alarm%20system%20panel%20glowing%20near%20front%20entrance%20manicured%20lawn%20mature%20trees%20Ontario%20Canada%20residential%20street%20warm%20evening%20light&width=800&height=400&seq=blog16&orientation=landscape"
                    alt="Best Alarm System Company Mississauga"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <span className="absolute top-4 left-4 bg-[#D4A853] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Alarm Systems Guide</span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors">Best Alarm System Company in Mississauga (2025)</h3>
                  <p className="text-white/65 text-sm mb-4 leading-relaxed">
                    Covers ULC-certified monitoring standards, Peel Regional Police priority dispatch, neighbourhood risk profiles from Malton to Lorne Park, and the five criteria that separate genuine Mississauga alarm specialists from generic GTA providers.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['ULC Monitoring', 'Peel Regional Police', 'Cellular Backup', 'Contract Terms'].map(tag => (
                      <span key={tag} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </a>

              {/* Home Security Company Guide */}
              <a href="/blog/best-home-security-company-mississauga" className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20Mississauga%20Ontario%20suburban%20home%20exterior%20at%20golden%20hour%20with%20professional%20security%20camera%20mounted%20near%20front%20door%20warm%20porch%20lighting%20manicured%20lawn%20quiet%20residential%20street%20safe%20community%20near%20Lake%20Ontario%20affluent%20neighbourhood%20clear%20blue%20sky%20Canadian%20architecture&width=800&height=400&seq=blog24&orientation=landscape"
                    alt="Best Home Security Company Mississauga"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <span className="absolute top-4 left-4 bg-[#D4A853] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Company Ranking Guide</span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors">Best Home Security Company in Mississauga (2026)</h3>
                  <p className="text-white/65 text-sm mb-4 leading-relaxed">
                    Rankings and buyer guidance for Mississauga homeowners — covering vehicle theft near the 401 corridor, Port Credit and Lorne Park risk profiles, condo vs. detached security configurations, and how ULC monitoring interacts with Peel Regional Police response times.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Home Security', 'Vehicle Theft', 'Peel Region', 'Condo Security'].map(tag => (
                      <span key={tag} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#D4A853] font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </a>
            </div>

            {/* Commercial Guide — full-width landscape card */}
            <a href="/blog/commercial-security-company-mississauga" className="group block bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
              <div className="flex flex-col md:flex-row">
                <div className="relative md:w-2/5 h-56 md:h-auto overflow-hidden flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Mississauga%20Ontario%20modern%20commercial%20and%20industrial%20district%20at%20dusk%20Airport%20Road%20corridor%20professional%20business%20park%20buildings%20security%20cameras%20mounted%20on%20exterior%20walls%20Pearson%20Airport%20cargo%20facilities%20visible%20in%20background%20clear%20evening%20sky%20Peel%20Region%20Canada%20urban%20skyline&width=800&height=500&seq=blog31&orientation=landscape"
                    alt="Best Commercial Security Company Mississauga"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/30 md:block hidden"></div>
                  <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Commercial Security Guide</span>
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#D4A853] transition-colors">
                    Best Commercial Security Company in Mississauga (2026)
                  </h3>
                  <p className="text-white/65 text-sm mb-4 leading-relaxed">
                    The definitive commercial security guide for Mississauga businesses — covering Canada&apos;s highest-concentration air cargo theft zone at Pearson Airport, the Highway 401 industrial belt break-in profile, organized retail crime at Square One and Heartland, corporate campus IP protection at Airport Corporate Centre, and Peel Regional Police commercial alarm protocols. The only guide that addresses all four of Mississauga&apos;s distinct commercial security environments.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Pearson Airport Cargo', 'Highway 401 Industrial', 'Square One ORC', 'Dock Access Control', 'Peel Regional Police', 'LPR Cameras'].map(tag => (
                      <span key={tag} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 text-[#D4A853] font-semibold group-hover:gap-3 transition-all">
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </a>

            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-white/10">
              {[
                { icon: 'ri-medal-line', label: 'Independent Research', desc: 'No sponsored rankings' },
                { icon: 'ri-map-pin-line', label: 'Mississauga-Specific', desc: 'Peel Region data only' },
                { icon: 'ri-refresh-line', label: 'Updated 2026', desc: 'Current PRP crime data' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="w-10 h-10 bg-[#D4A853]/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className={`${item.icon} text-[#D4A853]`}></i>
                  </div>
                  <p className="text-white text-sm font-semibold">{item.label}</p>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Mississauga</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 1-Hour Response',
                  description: 'Our main office is right here in Mississauga at 2355 Derry Road East — not a distant call centre. Mississauga emergencies get the fastest response in the GTA.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Mississauga Bylaw Knowledge',
                  description: 'We know Peel Region\'s alarm permit requirements, false alarm bylaws, and City of Mississauga regulations — so your system is always fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance secured our Port Credit home and our Square One office — same-day service, and they knew exactly what Mississauga condo boards require." — Mississauga client',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers including Intact and Aviva.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHY MISSISSAUGA TRUSTS US
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Mississauga's Most Trusted Security Company
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  With our main office located at 2355 Derry Road East, we're your true local security experts in Mississauga.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Main office in Mississauga - fastest response times',
                    'Licensed and insured Mississauga security professionals',
                    'Same-day service available across all neighborhoods',
                    'Factory-certified technicians for all major brands',
                    '24/7 local monitoring and support',
                    'Free on-site security assessments'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20modern%20alarm%20system%20in%20contemporary%20Mississauga%20home%20interior%20clean%20workspace%20expert%20installation&width=800&height=600&seq=mississauga2&orientation=landscape" 
                  alt="Security Installation Mississauga"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SERVICE COVERAGE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Mississauga Neighborhoods We Serve
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Providing security services across all Mississauga neighborhoods
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"
                >
                  <span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office & Map */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OUR OFFICE & SERVICE AREA</span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">Visit Our Mississauga Location</h2>
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {[
                    { icon: 'ri-map-pin-line', label: 'Address', content: '2355 Derry Road East, Unit 1\nMississauga, ON L5S 1V6' },
                    { icon: 'ri-phone-line', label: 'Phone', content: '1-888-458-9181', isPhone: true },
                    { icon: 'ri-time-line', label: 'Hours', content: 'Mon–Fri: 8:00 AM – 6:00 PM\nSat: 9:00 AM – 2:00 PM\nSun: Closed' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#D4A853] text-lg sm:text-xl`}></i>
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A1628] mb-1 text-sm sm:text-base">{item.label}</p>
                        {item.isPhone ? (
                          <a href="tel:18884589181" className="text-[#D4A853] hover:text-[#B8923D] font-semibold text-sm sm:text-base cursor-pointer">{item.content}</a>
                        ) : (
                          <p className="text-gray-600 text-xs sm:text-sm whitespace-pre-line">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
                  Schedule Consultation <i className="ri-calendar-line"></i>
                </Link>
              </div>
              <div className="relative w-full h-[300px] sm:h-[450px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92276.24!2d-79.6441!3d43.5890!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b469fe76b05b7%3A0x3146cbed75966db!2sMississauga%2C%20ON!5e0!3m2!1sen!2sca!4v1699999999999!5m2!1sen!2sca"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Mississauga Office Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Secure Your Mississauga Property?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
              Get a free security assessment from Mississauga's local security experts
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/#contact"
                className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Schedule Free Consultation
              </Link>
              <a 
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Commercial Security Cross-Link Banner */}
        <section className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <i className="ri-building-2-line text-white text-2xl"></i>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Looking for Commercial Security in Mississauga?</h3>
                </div>
                <p className="text-white/90 text-sm sm:text-base">
                  Explore our dedicated commercial security solutions for Mississauga businesses — cameras, alarms, access control & monitoring.
                </p>
              </div>
              <Link
                to="/commercial-security/mississauga"
                className="bg-white text-[#D4A853] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center gap-2"
              >
                View Commercial Security Mississauga <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Brampton', slug: 'brampton' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/service-areas/${city.slug}`}
                  className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Mississauga Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
